import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Relate } from '../../../../objects/relate';
import { first, switchMap, tap } from 'rxjs/operators';
import { RecordService } from '../../../../services/record.service';
import { MatDialog } from '@angular/material';
import { StaticFoldersComponent } from '../static-folders.component';
import { NgSelectComponent } from '@ng-select/ng-select';
import { LooseObject } from '../../../../objects/loose-object';
import { filled } from '../../../../shared/utils/common';
import { get } from 'lodash-es';
import { ListingService } from '../../../../services/listing.service';
import { forkJoin, of } from 'rxjs';
import { sprintf } from 'sprintf-js';
import { AssetTypeInspectionChecklistComponent } from '../asset-type-inspection-checklist/asset-type-inspection-checklist.component';
import { QuoteLineGroup } from '../../../../objects/quote-line-group';
import { UUID } from 'angular2-uuid';

@Component({
  selector: 'browse-products',
  templateUrl: './browse-products.component.html',
  styleUrls: ['./browse-products.component.scss']
})
export class BrowseProductsComponent implements OnInit, OnChanges {

  /**
   * This will emit the selected product.
   *
   * @var EventEmitter
   */
  @Output() objProductSelected = new EventEmitter<any>();

  /**
   * This will emit the selected group.
   *
   * @var EventEmitter
   */
  @Output() objGroupSelected = new EventEmitter<any>();

  /**
   * If the product to search are only labor true products.
   *
   * @var {boolean}
   */
  @Input() bLabor: boolean = null;

  /**
   * If we should allow searchable products.
   *
   * @var {boolean}
   */
  @Input() bSearchable: boolean = true;

  /**
   * If we should allow searchable products.
   *
   * @var {boolean}
   */
  @Input() bAllowCustom: boolean = true;

  /**
   * If the product has a pricebook filter.
   *
   * @var {boolean}
   */
  @Input() strPricebookId: string = null;

  /**
   * If the product to search are only sales true products.
   *
   * @var {boolean}
   */
  @Input() bSales: boolean = true;

  /// apply customer settings to searched items
  @Input('customerId') strCustomerId: string;

  /// override unit price with discount price
  @Input('overrideUnitPriceWithDiscountPrice') bOverrideUnitPriceWithDiscountPrice: boolean = true;

  /**
   * Hides the quantity input field in folder view.
   * We have this option because not all line items
   * have quantity.
   *
   * @var {boolean}
   */
  @Input() bHideQuantity: boolean = false;

  /**
   * determine if asset type is searchable
   *
   * @var {boolean}
   */
  @Input() bHasAssetType: boolean = false;

  /**
   * to determine if there is a contract pricing on the item
   *
   * @var {boolean}
   */
  @Input() strContractId: string = null;

  /**
   * Instead of the former per line product relate, we reverted into
   * a single product search which they can add to the list.
   *
   * @var {Relate<any>}
   */
  public objProductRelate = new Relate<any>();

  /**
   * asset type relate field
   *
   * @var {Relate<any>}
   */
  public objAssetType = new Relate<any>();

  constructor(
    public recordService: RecordService,
    private dialog: MatDialog,
    public list: ListingService,
  ) { }

  ngOnChanges(objChanges: SimpleChanges): void {
    if (objChanges && (filled(get(objChanges, 'strCustomerId')) || filled(get(objChanges, 'strPricebookId')))) {
      this.resetSearch();
    }
  }

  ngOnInit() {
    this.resetSearch();
  }

  /**
   * Reset the relates to a new value.
   *
   * @returns {void}
   */
  resetSearch(): void {

    let objFilters: LooseObject = {active: true};

    if (this.bSearchable) {
      objFilters.is_searchable = this.bSearchable
    }

    if (this.bLabor == null) {
      this.objProductRelate.buildRelates(
        switchMap(term => this.getRecords(term, objFilters, true, this.strPricebookId, this.strContractId))
      );
    } else if (this.bLabor === true) {

      objFilters.labor = true;
      this.objProductRelate.buildRelates(
        switchMap(term => this.getRecords(term, objFilters, false, this.strPricebookId, this.strContractId))
      );
    } else {

      objFilters.labor = false;
      this.objProductRelate.buildRelates(
        switchMap(term => this.getRecords(term, objFilters, this.bSales, this.strPricebookId, this.strContractId))
      );
    }
  }

  /**
   * Fetch the item using global ES instead of list filters.
   *
   * @param strTerm
   * @param objFilters
   * @param strIds
   * @param isSale
   * @param strPricebook
   *
   * @returns {Observable<any[]>}
   */
  getRecords(strTerm: string, objFilters: any, isSale: boolean, strPricebook: string, strContractId: string) {
    let productData = this.recordService.getProductRecordData(
      strTerm, objFilters, this.strCustomerId, isSale, strPricebook, [], false, this.bOverrideUnitPriceWithDiscountPrice, strContractId
    ).pipe(
      switchMap((productsResponse) =>  of(
        productsResponse.map( product => {
          product['module'] = 'items'
          return product;
        })
      ))
    );

    if (this.bHasAssetType) {
      let assetTypeData = this.recordService.getRecordRelate('asset_types', strTerm, '', false);
      return forkJoin([productData, assetTypeData]).pipe(
        switchMap(([productsResponse, assetTypesResponse]) => {
          const assetTypes = assetTypesResponse.map( assetType => {
            assetType['module'] = 'asset_types'
            return assetType;
          });
          return of(productsResponse.concat(assetTypes));
        }),
      );
    }

    return productData;
  }

  /**
   * This is triggered when creating a customized line item
   * and not getting from the list of items.
   *
   * @param name - name of the custom product.
   */
  addTag(name) {
    return { description: name };
  }

  /**
   * When an item is added, it triggers an event that informs parent.
   *
   * @param element
   * @param event
   */
  addAttribute(element: NgSelectComponent = null, event: any = null) {
    if (element) {
      element.handleClearClick();
    }

    if ((element && event) || (!element && event) || (!element && !event)) {
      if (event.module == 'asset_types') {
        this.dialog.open(AssetTypeInspectionChecklistComponent, {
          width: '600px',
          height: 'auto',
          data: event,
          disableClose: true
        })
        .afterClosed()
        .pipe(
          first(),
        )
        .subscribe(response => {
          if (filled(response)) {
            this.objGroupSelected.next(new QuoteLineGroup({
              select_line_id: UUID.UUID(),
              label: get(event, 'name'),
              is_group: true,
              module: 'asset_types',
              asset_type_id: event.id,
            }));
            response.forEach( item => {
              this.objProductSelected.next(item);
            });
          }
        });
      } else {
        this.objProductSelected.next(event);
      }
    }
  }

  /**
   * Let's open the static folder dialog so they
   * can select products using a folder like structure.
   *
   * TODO: Add type cast so we identify if label or group via a flag
   * instead of checking if the returned data has a "code" property.
   *
   * @returns {void}
   */
  openProductFolders(): void {

    let objStaticFolders = this.dialog.open(StaticFoldersComponent, {height: '70%', width: '70%', data: {
      labor: this.bLabor,
      hideQuantity: this.bHideQuantity,
      customer_id: this.strCustomerId,
      pricebook_id: this.strPricebookId,
      override_unit_price_with_discount_price: this.bOverrideUnitPriceWithDiscountPrice,
      sales: this.bSales,
    }});

    let objFolderSub = objStaticFolders.componentInstance.objProductSelected.subscribe(response => {
      this.objProductSelected.next(response);
    });

    objStaticFolders
      .afterClosed()
      .pipe(tap(() => {
        objFolderSub.unsubscribe();
      }))
      .subscribe();
  }

  generateLink(item: LooseObject): string {
    let modulePath = item.module;

    if (item.module == 'asset_types') {
      modulePath = 'admin/asset_types';
    }

    return sprintf('/#/%s/%s', modulePath, item.id);
  }
}
